import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  Grid,
  InputLabel,
} from "@mui/material";
import { useSnackbar } from "notistack";
import sh from "bundles/common/utils/sh";
import { replaceCurrentRecruiter } from "bundles/Recruiter/constants/recruiterConstants";
import ConfirmModal from "bundles/common/components/ConfirmModal";

// Rounded outlined inputs with a green focus/hover border (matches the app design system).
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(153, 68%, 25%)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(153, 68%, 25%)",
  },
  "& label.Mui-focused": {
    color: "hsl(153, 68%, 25%)",
  },
};

const EditCustomSkillsModal = ({ skill, closeModal }) => {
  const isEdit = skill != null;
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const dispatch = useDispatch();

  const initialValues = {
    id: skill?.id,
    name: skill?.name || "",
    category: skill?.category || "hard",
  };

  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    let required = (
      <FormattedMessage id="form_validation.required"></FormattedMessage>
    );

    if ("name" in fieldValues) {
      temp.name = fieldValues.name ? "" : required;
    }

    if ("category" in fieldValues) {
      temp.category = fieldValues.category ? "" : required;
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    let newValues = { ...values, [name]: value };
    setValues(newValues);
    validate({ [name]: value });
  };

  const handleSuccessfulSave = () => {
    replaceCurrentRecruiter(dispatch, authenticityToken, () => {
      closeModal();
      enqueueSnackbar(intl.formatMessage({ id: "settings.save_success" }), {
        variant: "success",
      });
    });
  };

  const handleFailedSave = (err) => {
    console.error(err);
    enqueueSnackbar(intl.formatMessage({ id: "settings.save_failed" }), {
      variant: "error",
    });
  };

  const handleFailedDelete = (err) => {
    console.error(err);
    enqueueSnackbar(intl.formatMessage({ id: "settings.delete_failed" }), {
      variant: "error",
    });
  };

  const handleSubmit = (e) => {
    setLoading(true);
    e.preventDefault();

    if (validate(values)) {
      let skill = {
        recruiter_id: currentRecruiter.id,
        id: values.id,
        name: values.name,
        category: values.category,
      };

      if (values.id) {
        sh.put(`skill/${skill.id}`, {
          authenticity_token: authenticityToken,
          skill,
        })
          .then(handleSuccessfulSave)
          .catch(handleFailedSave)
          .finally(() => {
            setLoading(false);
          });
      } else {
        sh.post("skill", {
          authenticity_token: authenticityToken,
          skill,
        })
          .then(handleSuccessfulSave)
          .catch(handleFailedSave)
          .finally(() => {
            setLoading(false);
          });
      }
    } else {
      setLoading(false);
    }
  };

  const handleDelete = (e) => {
    e.preventDefault();
    setIsConfirmModalOpen(true);
  };

  const handleDeleteConfirmed = (e) => {
    e.preventDefault();

    sh.delete(`skill/${skill.id}`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      data: {
        authenticity_token: authenticityToken,
        skill: skill,
      },
    })
      .then(handleSuccessfulSave)
      .catch(handleFailedDelete)
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        borderRadius: "16px",
        boxShadow: "0 20px 60px rgba(2, 51, 49, 0.18)",
        p: { xs: "24px", md: "40px 48px" },
        width: { xs: "calc(100% - 32px)", md: "600px" },
        maxWidth: "600px",
        maxHeight: "90vh",
        overflowY: "auto",
      }}
    >
      <form>
        <Box
          component="h2"
          sx={{
            textAlign: "center",
            fontFamily: "Segoe UI Bold, system-ui, sans-serif",
            fontSize: "1.25rem",
            color: "hsl(178, 93%, 10%)",
            letterSpacing: "-0.01em",
            m: 0,
            mb: "28px",
          }}
        >
          <FormattedMessage
            id={
              isEdit ? "settings.custom_skills_edit" : "settings.custom_skills_add"
            }
          />
        </Box>
        <TextField
          fullWidth
          required
          sx={{ ...inputSx, mb: "20px" }}
          InputLabelProps={{ required: true }}
          type="text"
          value={values.name}
          name="name"
          onBlur={handleInputValue}
          onChange={handleInputValue}
          variant="outlined"
          label={<FormattedMessage id="settings.custom_skills_name" />}
          {...(errors["name"] && {
            error: true,
            helperText: errors["name"],
          })}
        />
        <FormControl
          fullWidth
          variant="outlined"
          required
          sx={{ ...inputSx, mb: "28px" }}
        >
          <InputLabel id="category-select-label">
            <FormattedMessage id="settings.custom_skills_category" />
          </InputLabel>
          <Select
            value={values.category || "hard"}
            disabled={true}
            onChange={handleInputValue}
            onBlur={handleInputValue}
            {...(errors["category"] && {
              error: true,
              helperText: errors["category"],
            })}
            labelId="category-select-label"
            label={<FormattedMessage id="settings.custom_skills_category" />}
            name="category"
          >
            <MenuItem key={1} value="hard">
              <FormattedMessage id="candidate.profile.skills.hard" />
            </MenuItem>
            <MenuItem key={2} value="soft">
              <FormattedMessage id="candidate.profile.skills.soft" />
            </MenuItem>
            <MenuItem key={3} value="lang">
              <FormattedMessage id="candidate.profile.skills.lang" />
            </MenuItem>
          </Select>
        </FormControl>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Button
              type="submit"
              fullWidth
              variant="rounded"
              disabled={loading}
              onClick={handleSubmit}
              sx={{
                textTransform: "none",
                fontFamily: "Segoe UI Semibold, sans-serif",
                fontSize: "0.95rem",
                py: 1,
                backgroundColor: "hsl(153 68% 25%)",
                color: "white",
                boxShadow: "none",
                transition: "all 180ms ease",
                "&:hover": {
                  backgroundColor: "hsl(153 68% 22%)",
                  boxShadow: "none",
                },
              }}
            >
              <FormattedMessage id="save" />
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            {isEdit ? (
              <Button
                fullWidth
                variant="rounded"
                color="dangerOutlined"
                disabled={loading}
                onClick={handleDelete}
                style={{ textTransform: "none" }}
              >
                <FormattedMessage id="delete" />
              </Button>
            ) : (
              <Button
                fullWidth
                variant="rounded"
                color="secondaryOutlined"
                onClick={closeModal}
                style={{ textTransform: "none" }}
              >
                <FormattedMessage id="cancel" />
              </Button>
            )}
          </Grid>
        </Grid>
      </form>
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        setIsOpen={setIsConfirmModalOpen}
        handleConfirm={handleDeleteConfirmed}
        labelId="settings.delete_skill_confirmation"
      />
    </Box>
  );
};

export default EditCustomSkillsModal;
